import { AdminPageHeader } from '@/components/patterns/admin-page'
import { StockImageForm } from '@/components/stock-images/StockImageForm'
import { Badge } from '@/components/ui/badge'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { useIsReadOnly } from '@/lib/casl'
import { stockImagesQueries, useStockImage } from '@/lib/services/stock-images/stock-images.query'
import { stockImageParamsSchema } from '@/lib/services/stock-images/stock-images.schema'
import { useMerchandisingTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Images } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/stock-images/$stockImageId')({
  component: StockImageDetailPage,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => stockImageParamsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'stock-images') || !context.ability.can('access', 'global')) {
      throw notFound()
    }

    const [stockImage] = await Promise.all([
      context.queryClient.ensureQueryData(
        stockImagesQueries.show({
          organizationId: params.orgId,
          stockImageId: params.stockImageId,
        })
      ),
      context.queryClient.ensureQueryData(
        stockImagesQueries.tags({ organizationId: params.orgId })
      ),
    ])

    return {
      stockImage: stockImage?.data ?? stockImage,
      isReadOnly: !context.ability.can('edit', 'stock-images'),
    }
  },
})

function getStockImageTitle(stockImage) {
  return stockImage?.title || stockImage?.latest_media?.name || `Stock image #${stockImage?.id}`
}

export function StockImageDetailPage() {
  const { stockImageId } = Route.useParams()
  const { data: stockImageResponse } = useStockImage({ stockImageId })
  const stockImage = stockImageResponse?.data ?? stockImageResponse
  const { isReadOnly } = useIsReadOnly()
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups()

  return (
    <div className="max-w-6xl space-y-5">
      <AdminPageHeader
        icon={Images}
        title={getStockImageTitle(stockImage)}
        description="Manage the metadata and tags florists use when browsing stock images."
        badges={
          <Badge variant={stockImage?.is_published ? 'success' : 'muted'}>
            {stockImage?.is_published ? 'Published' : 'Unpublished'}
          </Badge>
        }
      />
      <StockImageForm
        key={`${stockImage?.id}-${isReadOnly}`}
        mode="edit"
        stockImage={stockImage}
        merchandisingTagGroups={merchandisingTagGroups}
      />
    </div>
  )
}
